import {
  AppShell,
  AppShellHeader,
  AppShellMain,
  Container,
  Group,
  MantineProvider,
  Pagination,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core"
import { DataFetchWrapper, Loader } from "@tfg/ui"
import _ from "lodash"
import { useCallback, useEffect } from "react"
import { useParams, useSearchParams } from "react-router-dom"

import classes from "~/assets/css/SearchEngine.module.css"
import { i18n } from "~/i18n"
import {
  usePublicDisplaySettingsQuery,
  usePublicProductsQuery,
} from "~/pages/Public/hooks"
import { buildPalette } from "~/pages/Public/utils"

import { ProductsGrid } from "./ProductsGrid"
import { SearchEngineHeader } from "./SearchEngineHeader"

export const PublicSearchEnginePage = () => {
  const params = useParams()
  const theme = useMantineTheme()
  const displaySlug = params.displaySlug as string

  const [searchParams, setSearchParams] = useSearchParams()

  const lang = searchParams.get("lang")

  const changeFilter = (key: string, value: string | null) => {
    setSearchParams((prev) => {
      if (value == null) {
        prev.delete(key)
      } else {
        prev.set(key, value)
      }
      return prev
    })
  }

  useEffect(() => {
    i18n.changeLanguage(lang ?? "fr")
  }, [lang])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleFilter = useCallback(_.debounce(changeFilter, 300), [])

  const {
    data: displaySetting,
    isLoading,
    isError,
  } = usePublicDisplaySettingsQuery(displaySlug)

  const extraParams = displaySetting?.filters
  const publicProductsQuery = usePublicProductsQuery(
    displaySetting?.companySlug,
    searchParams,
    extraParams
  )
  if (isError) {
    return (
      <Group justify="center" pt="xl">
        <Text style={{ color: "red" }}>Error</Text>
      </Group>
    )
  }
  if (isLoading) {
    return <Loader fullPage />
  }

  const lightColors = displaySetting?.outputBackgroundColor
    ? buildPalette(displaySetting.outputBackgroundColor)
    : theme.colors.beige
  const darkColors = displaySetting?.outputTitlesColor
    ? buildPalette(displaySetting.outputTitlesColor)
    : theme.colors.green
  const primaryColor =
    displaySetting?.outputTitlesColor ?? theme.colors.green[6]

  return (
    <MantineProvider
      theme={{
        ...theme,
        fontFamily: displaySetting?.outputFontFamily ?? theme.fontFamily,
        headings: {
          fontFamily: displaySetting?.outputFontFamily ?? theme.fontFamily,
        },
        colors: {
          ...theme.colors,
          primary: darkColors,
          secondary: lightColors,
        },
      }}
    >
      <AppShell
        padding={0}
        layout="alt"
        classNames={classes}
        header={{ height: { base: "18rem", xs: "16rem", sm: "12rem" } }}
      >
        <AppShellHeader className={classes.header}>
          <SearchEngineHeader
            values={searchParams}
            types={publicProductsQuery.data?.types ?? []}
            collections={publicProductsQuery.data?.collections ?? []}
            onFilter={handleFilter}
            brands={publicProductsQuery.data?.brands ?? []}
            searchEngineBrands={
              publicProductsQuery.data?.searchEngineBrands ?? []
            }
            displaySetting={displaySetting}
          />
        </AppShellHeader>
        <AppShellMain>
          <Container size="xl" my="xl">
            <DataFetchWrapper query={publicProductsQuery}>
              {({ data }) => (
                <Stack>
                  <Group justify="right">
                    <Pagination
                      color={primaryColor}
                      size="sm"
                      value={data.page}
                      onChange={(page) => {
                        setSearchParams((prev) => {
                          prev.set("page", page.toString())
                          return prev
                        })
                      }}
                      total={data.totalPages}
                    />
                  </Group>
                  <ProductsGrid
                    products={data.products}
                    primaryColor={primaryColor}
                  />
                  <Group justify="right">
                    <Pagination
                      color={primaryColor}
                      size="sm"
                      value={data.page}
                      onChange={(page) => {
                        setSearchParams((prev) => {
                          prev.set("page", page.toString())
                          return prev
                        })
                      }}
                      total={data.totalPages}
                    />
                  </Group>
                </Stack>
              )}
            </DataFetchWrapper>
          </Container>
        </AppShellMain>
      </AppShell>
    </MantineProvider>
  )
}
